import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder } from '../../services/api';
import { useApiQuery } from '../../hooks/useApiQuery';
import useToast from '../../hooks/useToast';
import { format } from 'date-fns';

const VendorBill = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();

  
  const {
    data: order,
    isLoading,
    isError,
  } = useApiQuery(
    ['order', id, 'bill'],
    () => getOrder(id).then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => {
        showError('Failed to load bill');
        navigate('/vendor/orders');
      },
    }
  );

  // ✅ Handle print
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading Bill...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (isError || !order) {
    return (
      <div className="text-center py-10 text-[#708090]">
        <p className="text-red-500">Order not found</p>
        <button
          onClick={() => navigate('/vendor/orders')}
          className="mt-4 text-[#1471b9] hover:underline"
        >
          Back to Orders
        </button>
      </div>
    );
  }

  // ✅ Format the order date
  const orderDate = order.paidAt || order.createdAt;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      {/* Bill Container - Receipt style */}
      <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-8">

        {/* Header */}
        <div className="text-center border-b border-[#dae1e7] pb-6 mb-6">
          <h1 className="text-2xl font-bold text-[#2b3445]">PAYMENT RECEIPT</h1>
          <p className="text-sm text-[#708090] mt-1">Order #{order._id.slice(-6)}</p>
        </div>

        {/* Customer & Vendor Info */}
        <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
          <div>
            <p className="text-[#708090] font-semibold">Customer</p>
            <p className="text-[#2b3445] font-medium">{order.attendeeId?.name || 'Walk-in'}</p>
            <p className="text-[#708090]">ID: {order.attendeeId?.customId || 'N/A'}</p>
          </div>
          <div className="text-right">
            <p className="text-[#708090] font-semibold">Date & Time</p>
            <p className="text-[#2b3445]">
              {format(new Date(orderDate), 'dd MMM yyyy, h:mm a')}
            </p>
            <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
              PAID
            </span>
          </div>
        </div>

        {/* Items Table */}
        <div className="border-y border-[#dae1e7] py-4 mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#708090] border-b border-[#dae1e7]">
                <th className="text-left py-2 font-medium">Item</th>
                <th className="text-center py-2 font-medium">Qty</th>
                <th className="text-right py-2 font-medium">Price</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, index) => (
                <tr key={index} className="border-b border-[#dae1e7] last:border-0">
                  <td className="py-3 text-[#2b3445]">
                    {item.foodItemId?.name || 'Item'}
                  </td>
                  <td className="py-3 text-center text-[#708090]">
                    x{item.quantity}
                  </td>
                  <td className="py-3 text-right font-medium">
                    ₹{(item.price * item.quantity).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end text-sm">
          <div className="w-1/2 space-y-1">
            <div className="flex justify-between text-[#708090]">
              <span>Subtotal</span>
              <span>₹{order.total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[#708090] border-t border-[#dae1e7] pt-2 mt-2">
              <span className="font-bold text-[#2b3445]">Total Paid</span>
              <span className="font-bold text-[#1471b9] text-lg">
                ₹{order.total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end gap-4">
          <button
            onClick={handlePrint}
            className="px-6 py-2 border border-[#dae1e7] rounded-[8px] text-sm font-medium hover:bg-[#f8f9fa] transition-colors"
          >
            🖨️ Print
          </button>
          <button
            onClick={() => navigate('/vendor/orders')}
            className="px-6 py-2 bg-[#1471b9] text-white rounded-[8px] text-sm font-bold hover:opacity-90 transition-opacity"
          >
            Back to Orders
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorBill;